import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import TootiDialog, { AlertGlyph } from '../components/common/Dialog'
import StatusBanner from '../components/common/StatusBanner'
import { BANNERS, TONE_TILE, alertDemos, demoToast } from '../lib/alerts'
import { appVersionLine } from '../lib/version'
import { useToastStore } from '../store/toastStore'

/**
 * The alert catalogue (design page 16, `alerts`; P-alerts of the go-live
 * plan): the three interruption levels the whole app uses — the modal
 * dialog, the inline status banner, the toast — each shown the way the
 * learner meets it. The six rows open the real dialogs from
 * `lib/alerts.js` with the design's sample numbers; a confirmation here
 * only says what it would have done. The logout row in particular signs
 * nobody out.
 */
export default function Alerts() {
  const { t } = useTranslation('alerts')
  const showToast = useToastStore((s) => s.show)
  const [open, setOpen] = useState(null)
  const demos = alertDemos()

  function confirm() {
    const demo = open
    setOpen(null)
    if (demo) showToast(demo.done)
  }

  return (
    <div className="mx-auto max-w-md space-y-3.5">
      <header className="flex items-center gap-2">
        <Link to="/settings" aria-label={t('page.backToSettings')} className="grid size-11 place-items-center rounded-xl text-ink hover:bg-surface-2">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </Link>
        <h1 className="flex-1 text-lg font-bold text-ink">{t('page.title')}</h1>
      </header>

      <p className="text-[12.5px] leading-[1.9] text-ink-2">{t('page.intro')}</p>

      <ul className="space-y-2">
        {demos.map((demo) => {
          const spec = demo.spec()
          return (
            <li key={demo.key}>
              <button
                type="button"
                onClick={() => setOpen(demo)}
                className="flex w-full items-center gap-3 card-soft px-[15px] py-3.5 text-start transition hover:bg-surface-2"
              >
                <span className={`grid size-9 shrink-0 place-items-center rounded-[11px] ${TONE_TILE[spec.tone]}`}>
                  <AlertGlyph icon={spec.icon} size={17} />
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="text-[13.5px] font-bold text-ink">{demo.label}</span>
                  <span className="text-[11.5px] text-ink-2">{demo.note}</span>
                </span>
                <span className="shrink-0 text-[11px] font-bold text-brand-600">{t('page.show')}</span>
              </button>
            </li>
          )
        })}
      </ul>

      <section className="space-y-2.5 pt-0.5">
        <h2 className="text-xs font-semibold text-ink-3">{t('page.bannersHeading')}</h2>
        <StatusBanner tone={BANNERS.offline.tone} icon={BANNERS.offline.icon}>
          {BANNERS.offline.text}
        </StatusBanner>
        <StatusBanner tone={BANNERS.payFailed.tone} icon={BANNERS.payFailed.icon}>
          {BANNERS.payFailed.text}
        </StatusBanner>
      </section>

      <button
        type="button"
        onClick={() => showToast(demoToast())}
        className="mt-0.5 h-[52px] w-full rounded-[14px] border-[1.5px] border-line bg-surface text-[13.5px] font-bold text-ink-2 transition hover:bg-surface-2"
      >
        {t('page.showToastButton')}
      </button>

      <p className="pt-1 text-center text-[11px] text-ink-3">{appVersionLine()}</p>

      {open && <TootiDialog spec={open.spec()} onConfirm={confirm} onDismiss={() => setOpen(null)} />}
    </div>
  )
}
